<template>
    <section class="tk-engagement-workspace">
        <TkEngagementWorkspaceHeader :engagement="engagement" />

        <div class="tk-engagement-workspace__toolbar">
            <q-tabs
                v-model="activePanel"
                class="tk-engagement-workspace__tabs"
                align="left"
                active-color="primary"
                indicator-color="primary"
                no-caps
                narrow-indicator
            >
                <q-tab name="overview">
                    <span class="tk-engagement-workspace__tab-content">
                        <q-icon name="o_person" />
                        <span>Client overview</span>
                    </span>
                </q-tab>
                <q-tab name="client-information">
                    <span class="tk-engagement-workspace__tab-content">
                        <q-icon name="badge" />
                        <span>Client information</span>
                    </span>
                </q-tab>
                <q-tab name="case-notes">
                    <span class="tk-engagement-workspace__tab-content">
                        <q-icon name="o_assignment" />
                        <span>Case notes</span>
                        <q-badge rounded color="grey-4" text-color="grey-8" :label="caseNoteCount" />
                    </span>
                </q-tab>
                <q-tab v-if="insightsAccessible" name="insights">
                    <span class="tk-engagement-workspace__tab-content">
                        <q-icon name="auto_awesome" />
                        <span>Insights</span>
                    </span>
                </q-tab>
                <q-tab name="attachments">
                    <span class="tk-engagement-workspace__tab-content">
                        <q-icon name="o_attach_file" />
                        <span>Attachments</span>
                    </span>
                </q-tab>
            </q-tabs>
            <slot name="actions" />
        </div>

        <div v-show="activePanel === 'overview'">
            <slot name="overview" />
        </div>
        <div v-if="visitedPanels['client-information']" v-show="activePanel === 'client-information'">
            <slot name="client-information" />
        </div>
        <div v-if="visitedPanels['case-notes']" v-show="activePanel === 'case-notes'">
            <slot name="case-notes" />
        </div>
        <div v-if="insightsAccessible && visitedPanels.insights" v-show="activePanel === 'insights'">
            <slot name="insights" />
        </div>
        <div v-if="visitedPanels.attachments" v-show="activePanel === 'attachments'">
            <slot name="attachments" />
        </div>
    </section>
</template>

<script>
import TkEngagementWorkspaceHeader from "@/components/TkClients/TkEngagementWorkspaceHeader.vue";

const PANEL_NAMES = ["overview", "client-information", "case-notes", "insights", "attachments"];

export default {
    name: "TkEngagementWorkspace",
    components: {
        TkEngagementWorkspaceHeader,
    },
    emits: ["update:modelValue"],
    props: {
        modelValue: {
            type: String,
            required: true,
            validator: value => PANEL_NAMES.includes(value),
        },
        engagement: {
            type: Object,
            required: true,
        },
        caseNoteCount: {
            type: Number,
            required: true,
        },
        insightsAccessible: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            visitedPanels: {
                overview: true,
                "client-information": this.modelValue === "client-information",
                "case-notes": this.modelValue === "case-notes",
                insights: this.modelValue === "insights",
                attachments: this.modelValue === "attachments",
            },
        };
    },
    computed: {
        activePanel: {
            get() {
                return this.modelValue;
            },
            set(value) {
                this.$emit(
                    "update:modelValue",
                    value === "insights" && !this.insightsAccessible ? "overview" : value
                );
            },
        },
    },
    watch: {
        modelValue: {
            immediate: true,
            handler(value) {
                this.ensureAccessiblePanel(value);
            },
        },
        insightsAccessible(value) {
            if (!value) this.ensureAccessiblePanel(this.modelValue);
        },
    },
    methods: {
        ensureAccessiblePanel(value) {
            if (value === "insights" && !this.insightsAccessible) {
                this.$emit("update:modelValue", "overview");
                return;
            }
            if (this.visitedPanels[value]) return;
            this.visitedPanels = {
                ...this.visitedPanels,
                [value]: true,
            };
        },
    },
};
</script>

<style scoped>
.tk-engagement-workspace__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin: 14px 24px 0;
}

.tk-engagement-workspace__tabs {
    width: fit-content;
    padding: 4px;
    border-radius: 9px;
    background: #eceee7;
}

.tk-engagement-workspace__tabs :deep(.q-tabs__content) {
    gap: 3px;
}

.tk-engagement-workspace__tabs :deep(.q-tab) {
    min-height: 36px;
    padding: 0 12px;
    border: 1px solid transparent;
    border-radius: 7px;
    color: #56635d;
}

.tk-engagement-workspace__tabs :deep(.q-tab--active) {
    border-color: #d9ddd5;
    background: white;
    color: #223830;
    box-shadow: 0 1px 3px rgba(47, 62, 70, 0.1);
}

.tk-engagement-workspace__tabs :deep(.q-tab__indicator) {
    display: none;
}

.tk-engagement-workspace__tab-content {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 0.82rem;
    font-weight: 550;
}

@media (max-width: 700px) {
    .tk-engagement-workspace__toolbar {
        align-items: flex-start;
        flex-direction: column;
        gap: 8px;
        margin: 12px 12px 0;
    }

    .tk-engagement-workspace__tabs {
        max-width: 100%;
        overflow-x: auto;
    }
}
</style>
